import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { format, isAfter, isBefore, isValid } from 'date-fns'
import deLocale from 'date-fns/locale/de'
import DOMPurify from 'dompurify'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken } from 'firebase/messaging'

export default defineNuxtPlugin((app) => {
  if (!app.vueApp.__my_mixin__) {
    app.vueApp.__my_mixin__ = true
    app.vueApp.mixin({
      computed: {
        defaultRotationTypes() {
          return app.$history.defaultRotationTypes
        },
        currentUser() {
          return app.$store.user
        },
        currentUserHasActiveTraining() {
          return hasActiveTraining(app.$store.user)
        },
        isNativePlatform() {
          return Capacitor.isNativePlatform()
        },
        selectedFacility() {
          return app.$facilityStore.id
        },
        selectedPlanningGroup() {
          return app.$planningGroupStore.id
        },
        facilityTree() {
          return app.$store.facilityTree
        },
        specialities() {
          return app.$store.specialities
        },
      },
      methods: {
        async login(email, password, otp) {
          localStorage.clear()
          const options = {}
          if (otp) {
            options.otp = otp
          }
          await app.$cms.login(email, password, options)
          if (Capacitor.isNativePlatform()) {
            const result = await PushNotifications.requestPermissions()
            if (result.receive === 'granted') {
              await PushNotifications.register()
            }

            // On success, we should be able to receive notifications
            await PushNotifications.addListener('registration', async (token) => {
              await this.addFcmToken(token.value)
            })
          } else {
            try {
              // Initialize Firebase
              const firebaseApp = initializeApp(app.$config.public.firebaseConfig)
              const messaging = getMessaging(firebaseApp)
              try {
                const currentToken = await getToken(messaging, {
                  vapidKey: app.$config.public.vapidKey,
                })
                if (currentToken) {
                  await this.addFcmToken(currentToken)
                }
              } catch (error) {
                console.warn('Could not get firebase token:', error)
              }
            } catch (reason) {
              console.warn('Could not get firebase token:', reason)
            }
          }
        },
        useLocalStorage(key) {
          const version = '1'
          const savedVersion = localStorage.getItem('localStorageVersion')
          if (version !== savedVersion) {
            const items = { ...localStorage }
            Object.keys(items).forEach((key) => {
              if (key.startsWith('/')) {
                localStorage.removeItem(key)
              }
            })
            localStorage.setItem('localStorageVersion', version)
          }

          const path = this.$route.path + '/'
          const savedValue = localStorage.getItem(path + key)
          if (savedValue) {
            this[key] = JSON.parse(savedValue)
          }
          this.$watch(
            key,
            (newValue) => {
              localStorage.setItem(path + key, JSON.stringify(newValue))
            },
            { deep: true },
          )
        },
        async useDatabaseStorage(key, options) {
          // ToDo: Find a way to avoid passing `this`
          await useDatabaseStorage(this, key, options)
        },
        async getUnitTypes(facilityId, withDefault) {
          let filterObject = {}
          if (facilityId) {
            filterObject = {
              filter: {
                facility: {
                  _eq: facilityId,
                },
              },
            }
          }

          const response = await app.$cms.request(
            app.$readItems('unit', {
              ...filterObject,
              fields: ['id', 'rotation_types'],
              sort: ['rotation_types'],
            }),
          )
          const unitRotationTypes = response.map((unit) => unit.rotation_types)
          const rotationTypes = unitRotationTypes.flat()
          if (withDefault) {
            rotationTypes.push(...this.defaultRotationTypes)
          }
          return [...new Set(rotationTypes)].filter((rotationType) => rotationType)
        },
        async addFcmToken(token) {
          const user = await app.$cms.request(
            app.$readMe({
              fields: ['id'],
            }),
          )
          const existingToken = await app.$cms.request(
            app.$readItems('fcm_token', {
              filter: {
                _and: [
                  {
                    token: {
                      _eq: token,
                    },
                  },
                  {
                    user: {
                      _eq: user.id,
                    },
                  },
                ],
              },
              fields: ['id', 'token'],
            }),
          )
          if (!existingToken.length) {
            await app.$cms.request(
              app.$createItem('fcm_token', {
                user: user.id,
                token,
              }),
            )
          }
        },
        hasRole(roles) {
          // ToDo: Replace every function call with a direct call to the following function
          return app.$store.hasRole(roles)
        },
        parseDate(dateString) {
          // This function is equivalent to `parseDate` in CMS
          // ToDo: Replace every function call with a direct call to the following function
          return parseDate(dateString)
        },
        formatDate(date, dateFormat) {
          if (date) {
            if (date === '2099-12-31') {
              return 'dauerhaft'
            }
            return format(this.parseDate(date), dateFormat || 'dd.MM.yyyy')
          }
        },
        toISODate,
        formatDateYear(date) {
          if (date) {
            if (date === '2099-12-31') {
              return ''
            }
            const parsedDate = new Date(date)
            if (!isValid(parsedDate)) {
              return ''
            }
            return format(parsedDate, ' yy')
          }
        },
        formatDateMonth(date) {
          if (date) {
            if (date === '2099-12-31') {
              return 'dauerhaft'
            }
            const parsedDate = new Date(date)
            if (!isValid(parsedDate)) {
              return ''
            }
            return format(parsedDate, 'dd. MMM', { locale: deLocale })
          }
        },
        formatMonthYear(date) {
          if (date) {
            return format(new Date(date), 'LLLL yyyy', { locale: deLocale })
          }
        },
        formatTime(date) {
          if (date) {
            return format(new Date(date), 'HH:mm')
          }
        },
        formatGender(gender) {
          if (gender) {
            switch (gender.toLowerCase()) {
              case 'female':
                return 'Frau'
              case 'male':
                return 'Herr'
              default:
                return ''
            }
          }
          return ''
        },
        formatNumber(value, digits = 1) {
          const formatter = new Intl.NumberFormat('de-DE', {
            maximumFractionDigits: digits,
          })
          return formatter.format(value)
        },
        formatCareType(type, noContent = '-') {
          switch (type) {
            case 'INPATIENT':
              return 'stationär'
            case 'OUTPATIENT':
              return 'ambulant'
            default:
              return noContent
          }
        },
        getQueryParams(options) {
          const directusOptions = {
            limit: options.itemsPerPage ? options.itemsPerPage : 10,
            page: options.page ? options.page : 1,
            // Meta: 'filter_count',
          }
          if (options.sortBy && options.sortBy.length && options.sortBy[0].key) {
            directusOptions.sort = options.sortBy.map((sort) => {
              return (sort.order === 'desc' ? '-' : '') + sort.key
            })
          }
          return directusOptions
        },
        // TODO: Build the first+last part via getUserDisplayName from @sediwork/shared
        // (keep the gender salutation, title, and email fallback here).
        formatUserName(user, showMail = true, showTitle = true) {
          let name = ''
          if (user) {
            const firstName = (user.first_name || '').trim()
            const lastName = (user.last_name || '').trim()
            if (firstName || lastName) {
              if (user.gender && showTitle) {
                switch (user.gender) {
                  case 'FEMALE':
                    name = 'Frau '
                    break
                  case 'MALE':
                    name = 'Herr '
                    break
                }
              }
              if (user.title && showTitle) {
                name = name + user.title + ' '
              }
              return name + `${firstName} ${lastName}`
            } else if (showMail) {
              return user.email
            }
          }
          return ''
        },
        getConfig(key) {
          return this.currentUser.configurations.find((config) => config.key === key)
        },
        async setConfig(key, value, additionalValue = null) {
          const config = this.getConfig(key)
          const user = JSON.parse(JSON.stringify(this.currentUser))
          if (config) {
            const updatedConfig = await app.$cms.request(
              app.$updateItem(
                'configuration',
                config.id,
                {
                  value,
                  additional_value: additionalValue,
                },
                {
                  fields: ['id', 'key', 'value', 'additional_value'],
                },
              ),
            )
            user.configurations = user.configurations.filter(
              (existingConfig) => existingConfig.id !== config.id,
            )
            user.configurations.push(updatedConfig)
          } else {
            const newConfig = await app.$cms.request(
              app.$createItem(
                'configuration',
                {
                  user: user.id,
                  key,
                  value,
                  additional_value: additionalValue,
                },
                {
                  fields: ['id', 'key', 'value', 'additional_value'],
                },
              ),
            )
            user.configurations.push(newConfig)
          }
          app.$store.user = user
        },
        dompurify(html) {
          return DOMPurify.sanitize(html)
        },
        async getPhysician(userId, fields = ['id']) {
          const physicians = await app.$cms.request(
            app.$readItems('physician', {
              filter: {
                user: {
                  _eq: userId,
                },
              },
              fields,
              limit: 1,
            }),
          )

          if (physicians.length) {
            return physicians[0]
          } else {
            return await app.$cms.request(
              app.$createItem('physician', { user: userId }, { fields }),
            )
          }
        },
        async fileUrl(id) {
          if (id) {
            const token = await app.$cms.getToken()
            return `${app.$config.public.cmsUrl}/assets/${id}?access_token=${token}`
          } else {
            return null
          }
        },
        formatParts(parts, separator, lastSeparator) {
          return joinParts(parts, separator, lastSeparator)
        },
        getStatusForTime(entry) {
          const now = new Date()
          const start = this.parseDate(entry.start)
          const end = this.parseDate(entry.end || '2099-12-31')
          if (isBefore(end, now)) {
            return 'past'
          } else if (isAfter(start, now)) {
            return 'future'
          } else {
            return 'current'
          }
        },
        isOwnFacility(facilityId) {
          return app.$store.isOwnFacility(facilityId)
        },
        // A user without any facility can only be deleted by a global planner of the same Verbund.
        mayDeleteUser(user) {
          if (user.facilities?.length) return true
          return mayDeleteUserWithoutFacility(
            {
              role: this.currentUser.role,
              mainFacilityId: getId(this.currentUser.main_facility),
              isMainFacilityAdmin: this.currentUser.main_facility_admin,
            },
            { role: user.role, mainFacilityId: getId(user.main_facility), facilityCount: 0 },
          )
        },
        isOwnPlanningGroup(planningGroupId) {
          return app.$store.isOwnPlanningGroup(planningGroupId)
        },
        getPremiumFeatures() {
          if (this.currentUser.main_facility && this.currentUser.main_facility.premium_features) {
            return this.currentUser.main_facility.premium_features
          } else {
            return []
          }
        },
        // Also deletes the user's avatar and documents.
        async deleteUser(userId) {
          await app.$cms.request(() => ({
            method: 'DELETE',
            path: `/custom-delete-user/users/${userId}`,
          }))
        },
        groupByProperty(objects, propertyName, multiple = false) {
          return objects.reduce(function (r, a) {
            if (multiple) {
              if (a[propertyName]) {
                a[propertyName].forEach((value) => {
                  r[value] = r[value] || []
                  r[value].push(a)
                })
              }
              return r
            } else {
              r[a[propertyName]] = r[a[propertyName]] || []
              r[a[propertyName]].push(a)
              return r
            }
          }, Object.create(null))
        },
        getColorForPercentage(value, max) {
          if (value && max) {
            const percent = value / max
            if (percent <= 0.25) {
              return 'yellow'
            } else if (percent <= 0.5) {
              return 'lime'
            } else if (percent <= 0.75) {
              return 'light-green'
            } else {
              return 'green'
            }
          }
          return 'grey'
        },
        formatFacilityHierarchy(facility, separator = ' > ') {
          if (!facility) {
            return ''
          }
          if (facility.parent_facility === null) {
            return facility.name
          }
          let currentFacility = JSON.parse(JSON.stringify(facility))
          const facilities = []
          while (currentFacility.parent_facility) {
            if (currentFacility.parent_facility.show_in_hierarchy) {
              facilities.unshift(currentFacility.parent_facility)
            }
            currentFacility = currentFacility.parent_facility
          }
          return this.formatParts(
            facilities.map((f) => f?.name),
            separator,
          )
        },
        getRequirementTitle(requirement, rindex) {
          let text = `${rindex && rindex > 0 ? 'und ' : ''}`
          if (requirement.minimum_months) {
            text = text + requirement.minimum_months + ' M. '
          }
          if (
            requirement.units?.length ||
            requirement.specialities?.length ||
            requirement.rotation_types?.length ||
            requirement.facilities?.length
          ) {
            text =
              text +
              `${this.formatParts(
                [
                  this.getNames(requirement.units, 'unit_id.short_name', 'Einheiten'),
                  this.getNames(requirement.specialities, 'speciality_id.name', 'Fachrichtungen'),
                  requirement.rotation_types?.length ? requirement.rotation_types.join(', ') : null,
                  this.getNames(requirement.facilities, 'facility_id.name', 'Kliniken'),
                ],
                ', ',
              )}`
          } else {
            text = text + 'Erfahrung jeglicher Art'
          }
          if (requirement.label) {
            return 'Label: ' + requirement.label.name
          }

          return getRequirementLimitText(requirement) || text
        },
        getNames(array, path, pluralName = '', maxlength = 3) {
          if (!array || !array.length) {
            return null
          } else if (array.length <= maxlength) {
            return this.formatParts(
              array.map((object) => this.getValueByPath(object, path)),
              ', ',
              ' oder ',
            )
          } else {
            return array.length + ' ' + pluralName
          }
        },
        isInTime(object, startDate, endDate, inc = true) {
          // This function is equivalent to the filter logic of `filterStartEnd` in CMS
          // ToDo: Replace every function call with a direct call to the following function
          return hasDateOverlap(object, startDate, endDate, inc)
        },
        getValueByPath(object, path) {
          let o = object
          if (path) {
            const a = path
              .replace(/\[(\w+)\]/g, '.$1')
              .replace(/^\./, '')
              .split('.')
            while (a.length) {
              const n = a.shift()
              // TODO: use typeof o === 'object'; comparing to the Object constructor never matches
              if (!o || (typeof o === Object && !(n in o))) return null
              o = o[n]
            }
          }
          return o
        },
        setValueByPath(obj, path, value) {
          // Split the path into an array of keys
          const keys = path.split('.')
          let current = obj

          // Iterate over the keys array, except for the last key
          for (let i = 0; i < keys.length - 1; i++) {
            let key = keys[i]

            // Check if the current key is an array index
            if (!isNaN(parseInt(key))) {
              key = parseInt(key) // Convert to a number if it's an index
            }

            // If the key doesn't exist or isn't an object/array, create an empty object at this key
            if (!current[key] || typeof current[key] !== 'object') {
              current[key] = isNaN(parseInt(keys[i + 1])) ? {} : [] // Use an array if the next key is a numeric index
            }

            // Move to the next level in the object
            current = current[key]
          }

          // Set the value at the final key
          const finalKey = keys[keys.length - 1]
          current[isNaN(parseInt(finalKey)) ? finalKey : parseInt(finalKey)] = value
        },
        getReasonTitle(reason) {
          switch (reason) {
            case 'flow_rotation':
              return 'Im Rotationscurriculum erreichbar'
            case 'flow_rotation_progress':
              return 'Fortgeschrittene Position im Rotationscurriculum'
            case 'flow_rotation_prio':
              return 'Priorität im Rotationscurriculum'
            case 'flow_requirement':
              return 'Benötigt für Weiterkommen im Rotationscurriculum'
            case 'regulation':
              return 'Benötigt laut Weiterbildungsordnung'
            case 'goals':
              return 'Benötigt für Ziele'
            case 'request':
              return 'Wunscheinheit des Rotanden'
            case 'request_history':
              return 'Abzug für bereits erfüllten Wunsch'
            case 'last_unit':
              return 'Dauer seit letztem Mal in Einheit'
            case 'last_facility':
              return 'Dauer seit letztem Mal in Klinik'
            case 'external':
              return 'Priorität Rotation gegenüber Einsatzplanung'
            case 'last_external':
              return 'Dauer seit letztem Mal in Rotation'
            case 'external_request':
              return 'Wunschklinik des Rotanden'
            case 'new_competence':
              return 'Neu erlernte Kompetenzen'
            case 'extension':
              return 'Mindestaufenthaltsdauer'
            case 'duration_limit':
              return 'Maximalaufenthaltsdauer'
            case 'stays':
              return 'Maximale Einteilungshäufigkeit'
            default:
              return 'Sonstige'
          }
        },
        getShortName(facility) {
          if (!facility) {
            // Garbage in, garbage out, what are we supposed to do here?
            return facility
          }

          if (typeof facility === 'object' && 'shortName' in facility && facility.shortName) {
            return facility.shortName
          }

          if (typeof facility === 'object' && 'short_name' in facility && facility.short_name) {
            return facility.short_name
          }

          const longName =
            typeof facility === 'string' || facility instanceof String ? facility : facility.name

          return longName
            .replaceAll(/(?<=[\p{Letter}])[\p{Lowercase_Letter}]/gu, '.')
            .replaceAll(/\.+/g, '.')
        },
        shortenEmail(email, maxLength = 25) {
          if (!email) return ''
          const [localPart] = email.split('@')
          if (localPart.length < maxLength) return email
          return `${localPart.slice(0, maxLength)}@...`
        },
        getRequirementHintText(requirement, startText = 'Alle Personen müssen ') {
          const minimumPercentage = parseInt(requirement.minimum_percentage)
          const minimumDuration = parseInt(requirement.minimum_duration)
          const maximumDuration = parseInt(requirement.maximum_duration)
          const maximumStays = parseInt(requirement.maximum_stays)
          const minimumUsers = parseInt(requirement.minimum_users)
          const minimumMonths = parseInt(requirement.minimum_months)

          let hintText = ''

          const hints = []
          if (minimumPercentage) {
            if (minimumPercentage === 100) {
              hints.push('in Vollzeit arbeiten')
            } else {
              hints.push(`mindestens ${minimumPercentage}% einer Vollzeitstelle arbeiten`)
            }
          }
          if (minimumDuration) {
            hints.push(`für mindestens ${minimumDuration} Monate eingeteilt werden`)
          }
          if (maximumDuration) {
            hints.push(`das Einteilungslimit von maximal ${maximumDuration} Monaten einhalten`)
          }
          if (maximumStays) {
            hints.push(`das Limit von maximal ${maximumStays} Einteilungen einhalten`)
          }
          if (requirement.label?.name.length) {
            hints.push(`dem Label '${requirement.label.name}' zugewiesen sein.`)
          }

          if (minimumUsers) {
            hintText =
              hintText +
              `Mindestens ${
                minimumUsers === 1 ? 'eine Person muss' : minimumUsers + ' Personen müssen'
              } `
          } else {
            hintText = hintText + startText
          }
          if (
            minimumMonths ||
            requirement.modelUnits?.length ||
            requirement.rotation_types?.length ||
            requirement.modelSpecialities?.length ||
            requirement.modelFacilities?.length
          ) {
            if (minimumMonths) {
              hintText =
                hintText + `für mindestens ${minimumMonths} Monat${minimumMonths === 1 ? '' : 'e'} `
            } else {
              hintText = hintText + 'für eine unbestimmte Zeit '
            }
            if (requirement.modelUnits?.length) {
              hintText =
                hintText +
                `in der Einheit ${this.getNames(
                  requirement.modelUnits,
                  'short_name',
                  '',
                  Infinity,
                )} `
            }
            if (requirement.rotation_types?.length) {
              hintText =
                hintText +
                `im Einheitstyp ${this.getNames(requirement.rotation_types, null, '', Infinity)} `
            }
            if (requirement.modelSpecialities?.length) {
              hintText =
                hintText +
                `im Bereich ${this.getNames(requirement.modelSpecialities, 'name', '', Infinity)} `
            }
            if (requirement.modelFacilities?.length) {
              hintText =
                hintText +
                `in der Klinik ${this.getNames(requirement.modelFacilities, 'name', '', Infinity)} `
            }
            hintText = hintText + 'gearbeitet haben.'

            if (hints.length) {
              hintText =
                hintText + `<br>Diese ${requirement.minimum_users === 1 ? 'muss' : 'müssen'} `
            }
          } else if (!hints.length) {
            return 'Keine Bedingungen definiert'
          }
          if (hints.length) {
            hintText = hintText + hints.join(' und ') + '.'
          }
          return hintText
        },
      },
    })
  }
})
